import React, { forwardRef } from "react";
import { Button } from "@progress/kendo-react-buttons";

/**
 * DKNButton — Daikin design system button.
 * Wraps Kendo React Button with project-wide defaults.
 *
 * Props (all Kendo Button props are forwarded):
 *   variant   "primary" | "secondary" | "danger" | "ghost"  (default: "primary")
 *   size      "small" | "medium" | "large"                  (default: "medium")
 *   fullWidth  boolean                                       (default: false)
 *   loading    boolean — shows a spinner and disables         (default: false)
 *   startIcon  ReactNode — icon before label
 *   endIcon    ReactNode — icon after label
 */
const VARIANT_MAP = {
  primary:   { themeColor: "primary",  fillMode: "solid"   },
  secondary: { themeColor: "base",     fillMode: "outline" },
  danger:    { themeColor: "error",    fillMode: "solid"   },
  ghost:     { themeColor: "base",     fillMode: "flat"    },
};

const SIZE_MAP = {
  small:  "small",
  medium: "medium",
  large:  "large",
};

const DKNButton = forwardRef(function DKNButton(
  {
    variant = "primary",
    size = "medium",
    fullWidth = false,
    loading = false,
    startIcon,
    endIcon,
    disabled,
    className = "",
    style,
    children,
    ...rest
  },
  ref
) {
  const { themeColor, fillMode } = VARIANT_MAP[variant] ?? VARIANT_MAP.primary;
  const kendoSize = SIZE_MAP[size] ?? "medium";

  return (
    <Button
      ref={ref}
      themeColor={themeColor}
      fillMode={fillMode}
      size={kendoSize}
      disabled={disabled || loading}
      className={`dkn-btn${fullWidth ? " dkn-btn-full" : ""} ${className}`}
      style={style}
      {...rest}
    >
      {startIcon && <span className="dkn-btn-icon dkn-btn-icon-start">{startIcon}</span>}
      {loading ? <span className="dkn-btn-spinner" aria-hidden="true" /> : children}
      {!loading && endIcon && <span className="dkn-btn-icon dkn-btn-icon-end">{endIcon}</span>}
    </Button>
  );
});

export default DKNButton;
